import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Form } from "antd";

import { ReactComponent as SaveSVG } from "@/assets/icons/feather-icons/save.svg";
import useAllTextLocations from "@/shared/components/assessment/AboutAssessment/hooks/useAllTextLocations";
import { Loading } from "@/shared/components/Loading/Loading";
import { SubmitStatuses } from "@/shared/components/testManagement/TestElementInfoBlock";
import style from "@/modules/admin/clientManagement/GeneralClientSettings/style.module.css";
import { useGetClient } from "@/modules/client/hooks/useGetClient";
import { ClientHttpService } from "@/modules/client/services/client-http.service";
import { Button, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { INPUT_MAX_LENGTH, MarkdownField } from "@/ui-components/commonComponents/MarkdownField/MarkdownField";
import LocaleContent from "@/ui-components/commonComponents/LocaleContent/LocaleContent";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { TextLocations } from "@/modules/assessments/scenes/CreateAssessment/createAssessment.constants";
import { ALL_SYSTEM_LOCALES, localeLabel } from "@/shared/utils/locale.utils";

export type UpdatedTextsType = Record<string, Record<string, string>>;

export default function ClientTextsSettings() {
  const { client_id: clientId } = useParams<{ client_id: string }>();
  const { client, isLoading: isClientLoading } = useGetClient(Number(clientId));
  const { textLocations, isLoading: isTextLocationsLoading } = useAllTextLocations();
  const [alert, setAlert] = useState<SubmitStatuses>(SubmitStatuses.initial);
  const [isUpdateLoading, setUpdateLoading] = useState<boolean>(false);
  const [updatedTexts, setUpdatedTexts] = useState<UpdatedTextsType>({});
  const onSave = async () => {
    try {
      setUpdateLoading(true);

      await ClientHttpService.updateClientTexts(Number(clientId), updatedTexts);
      setAlert(SubmitStatuses.success);
    } catch (err) {
      setAlert(SubmitStatuses.error);
    } finally {
      setUpdateLoading(false);
    }
  };
  const getAlert = (status: SubmitStatuses) => {
    switch (status) {
      case SubmitStatuses.error:
        return <Alert showIcon message="Please provide valid content for every available language." type="error" />;
      case SubmitStatuses.success:
        return <Alert showIcon message="Success" type="success" />;
      case SubmitStatuses.initial:
      default:
        return;
    }
  };

  useEffect(() => {
    setUpdatedTexts(
      client?.clientTexts?.reduce<UpdatedTextsType>((acc, { locationId, body }) => {
        acc[locationId] = body ?? {};

        return acc;
      }, {}) ?? {},
    );
  }, [client?.clientTexts]);

  const clientAbility = useContext(ClientAbilityContext);
  const canUpdateClientText = clientAbility.can(Action.Update, Subject.CLIENT_TEXT, "text");

  if (isClientLoading || isTextLocationsLoading) {
    return <Loading />;
  }

  return (
    <div className={style.container}>
      {textLocations.map((location) => {
        // Landing text is not configurable at the Client-level. It is specific to a job-family, not a client.
        if (location.name === TextLocations.landingText) return null;

        return (
          <div style={{ marginBottom: 30 }} key={location.locationId}>
            <h2>{location.name}</h2>

            <LocaleContent
              locales={ALL_SYSTEM_LOCALES}
              renderLabel={(locale) => localeLabel(locale)}
              renderPane={(locale) => {
                const textValue = updatedTexts?.[location.locationId]?.[locale] || "";
                return (
                  <Form.Item className={style.mdEditor} data-length={`${textValue.length} / ${INPUT_MAX_LENGTH}`}>
                    <MarkdownField
                      height={300}
                      value={textValue}
                      onChange={(value) => {
                        setUpdatedTexts((prevTexts) => ({
                          ...prevTexts,
                          [location.locationId]: { ...prevTexts[location.locationId], [locale]: value ?? "" },
                        }));
                      }}
                    />
                  </Form.Item>
                );
              }}
            />
          </div>
        );
      })}

      <div className={style.buttonContainer}>
        <Button
          icon={<SaveSVG className={style.svgSize} aria-hidden="true" />}
          buttonSize={ButtonSize.XSmall}
          disabled={!Object.keys(updatedTexts).length || !canUpdateClientText}
          onClick={onSave}
          isLoading={isUpdateLoading}
        >
          Save
        </Button>

        <div className={style.alertContainer}>{getAlert(alert)}</div>
      </div>
    </div>
  );
}
